import {
  createContext,
  useState,
  type PropsWithChildren,
  type Dispatch,
  type FC,
  useContext,
} from 'react';
import { consoleEvent } from '@/utils/console-event';
import type {
  DBSScanProcessedData,
  PeopleConnectResponseItemDob,
} from '@/api/dbs/dbs.types';

export type DbooState = {
  email: string;
  firstName: string;
  lastName: string;
  city: string;
  state: string;
  phoneNumber: string;
  middleName: string;
  address: string;
  zip: string;
  dob: string;
  brokers: DBSScanProcessedData;
};

type DbooContextValue = {
  state: DbooState;
  updateState: Dispatch<Partial<DbooState>>;
};

const defaultState: DbooState = {
  email: '',
  firstName: '',
  lastName: '',
  city: '',
  state: '',
  phoneNumber: '',
  middleName: '',
  address: '',
  zip: '',
  dob: '',
  brokers: {
    profiles: [],
    dob: {} as PeopleConnectResponseItemDob,
  },
};

const defaultContextValue: DbooContextValue = {
  state: defaultState,
  updateState: () => {
    consoleEvent({
      type: 'warn',
      notifyMessage: 'updateDbooState not initialized',
      message: 'DbooContext',
    });
  },
};

export const DbooContext = createContext<DbooContextValue>(defaultContextValue);

type DbooContextProviderProps = PropsWithChildren & {
  providedState?: Partial<DbooState>;
};

export const DbooContextProvider: FC<DbooContextProviderProps> = ({
  children,
  providedState,
}) => {
  const [dbooState, setDbooState] = useState<DbooState>({
    ...defaultState,
    ...providedState,
  });

  const updateDbooState = (dbooState: Partial<DbooState>) => {
    setDbooState(prevState => ({
      ...prevState,
      ...dbooState,
    }));
  };

  return (
    <DbooContext.Provider
      value={{ state: dbooState, updateState: updateDbooState }}
    >
      {children}
    </DbooContext.Provider>
  );
};

export const useDbooContext = (): DbooContextValue => {
  const context = useContext(DbooContext);
  if (!context) {
    throw new Error('useDbooContext must be used within a DbooProvider');
  }
  return context;
};
